import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Dashboard from "@/pages/Dashboard";
import Workspace from "@/pages/Workspace";
import Login from "@/pages/Login";
import ReaderPage from "@/pages/ReaderPage";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import Pricing from "@/pages/Pricing";
import BillingReturn from "@/pages/BillingReturn";
import AdminInbox from "@/pages/AdminInbox";
import Settings from "@/pages/Settings";
import Faq from "@/pages/Faq";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import DeleteAccount from "@/pages/legal/DeleteAccount";
import { I18nProvider } from "@/lib/i18n";
import { GateProvider } from "@/lib/gate";
import { Loader2, Feather } from "lucide-react";

function AppRouter() {
    return (
        <Routes>
            <Route path="/read/:token" element={<ReaderPage />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/faq" element={<Faq />} />
            {/* Public by design: Google Play requires these to load signed out. */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/pricing" element={<Guarded><Pricing /></Guarded>} />
            <Route path="/billing/return" element={<Guarded><BillingReturn /></Guarded>} />
            <Route path="/" element={<Guarded><Dashboard /></Guarded>} />
            <Route path="/novel/:novelId" element={<Guarded><Workspace /></Guarded>} />
            <Route path="/admin" element={<Guarded><AdminInbox /></Guarded>} />
            <Route path="/settings" element={<Guarded><Settings /></Guarded>} />
        </Routes>
    );
}

function Guarded({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="h-screen flex flex-col items-center justify-center text-muted-foreground">
                <div className="flex items-center gap-2 font-heading text-2xl mb-3">
                    <Feather className="w-5 h-5 text-primary" /> Day Dreamer
                </div>
                <div className="flex items-center gap-2 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                </div>
            </div>
        );
    }
    if (!user) return <Login />;
    return children;
}

function App() {
    useEffect(() => { document.title = "Day Dreamer by H.Tokoki"; }, []);
    return (
        <div className="App min-h-screen bg-background text-foreground">
            <BrowserRouter>
                <AuthProvider>
                    <I18nProvider>
                        <GateProvider>
                            <AppRouter />
                        </GateProvider>
                    </I18nProvider>
                </AuthProvider>
            </BrowserRouter>
            <Toaster position="bottom-right" />
        </div>
    );
}

export default App;
